import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Tag, TagsIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ColumnDef, ColumnSizingState } from "@tanstack/react-table";
import { supabase } from "@/integrations/supabase/client";
import { DataTable } from "@/components/ui/data-table";
import { StatusCell, DateTimeCell, SortableHeader } from "@/components/ui/table-cells";
import { AvailableUpdates } from "@/components/patching/AvailableUpdates";
import { CreatePatchPolicyDialog } from "@/components/patching/CreatePatchPolicyDialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { TagAssignmentDialog } from "@/components/tags/TagAssignmentDialog";
import { TagRemovalDialog } from "@/components/tags/TagRemovalDialog";
import { TagsDisplay } from "@/components/tags/TagsDisplay";
import { ActionsDropdown, ActionItem } from "@/components/ui/actions-dropdown";

type PatchPolicy = {
  id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  schedule_type: 'immediate' | 'scheduled' | 'manual';
  schedule_time: string | null;
  restart_required: boolean;
  defer_max_times: number;
  defer_duration_minutes: number;
  created_at: string;
  updated_at: string;
};

export default function PatchingPage() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [tagAssignmentOpen, setTagAssignmentOpen] = useState(false);
  const [tagRemovalOpen, setTagRemovalOpen] = useState(false);
  const [selectedPolicyId, setSelectedPolicyId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: policies, isLoading, error } = useQuery({
    queryKey: ["patch-policies"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("patch_policies")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as PatchPolicy[];
    },
  });

  const handlePolicyCreated = () => {
    setIsCreateDialogOpen(false);
  };

  const handleToggleEnabled = async (policyId: string, newValue: boolean) => {
    try {
      const { error } = await supabase
        .from("patch_policies")
        .update({ enabled: newValue })
        .eq("id", policyId);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ["patch-policies"] });
      toast.success(`Policy ${newValue ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      console.error("Error updating policy:", error);
      toast.error("Failed to update policy");
    }
  };

  const columns: ColumnDef<PatchPolicy>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => <SortableHeader column={column}>Name</SortableHeader>,
      cell: ({ row }) => (
        <div className="font-medium">{row.getValue("name")}</div>
      ),
    },
    {
      accessorKey: "schedule_type",
      header: ({ column }) => <SortableHeader column={column}>Schedule Type</SortableHeader>,
      cell: ({ row }) => (
        <div className="capitalize">{row.getValue("schedule_type")}</div>
      ),
    },
    {
      accessorKey: "restart_required",
      header: ({ column }) => <SortableHeader column={column}>Restart Required</SortableHeader>,
      cell: ({ row }) => (
        <div>{row.getValue("restart_required") ? "Yes" : "No"}</div>
      ),
    },
    {
      accessorKey: "enabled",
      header: ({ column }) => <SortableHeader column={column}>Status</SortableHeader>,
      cell: ({ row }) => (
        <StatusCell 
          status={row.getValue("enabled") ? "enabled" : "disabled"}
        />
      ),
    },
    {
      accessorKey: "created_at",
      header: ({ column }) => <SortableHeader column={column}>Created</SortableHeader>,
      cell: ({ row }) => (
        <DateTimeCell date={row.getValue("created_at")} format="date" />
      ),
    },
    {
      id: "tags",
      header: "Tags",
      cell: ({ row }) => (
        <TagsDisplay
          resourceId={row.original.id}
          resourceType="patch_policy"
          maxTags={2}
          showRemoveButton={false}
        />
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const [isUpdating, setIsUpdating] = useState(false);
        
        const handleToggle = async (checked: boolean) => {
          setIsUpdating(true);
          await handleToggleEnabled(row.original.id, checked);
          setIsUpdating(false);
        };

        const actions: ActionItem[] = [
          {
            label: "Assign Tags",
            icon: Tag,
            onClick: () => {
              setSelectedPolicyId(row.original.id);
              setTagAssignmentOpen(true);
            },
          },
          {
            label: "Remove Tags",
            icon: TagsIcon,
            onClick: () => {
              setSelectedPolicyId(row.original.id);
              setTagRemovalOpen(true);
            },
          },
        ];
        
        return (
          <div className="flex items-center gap-2">
            <Switch 
              checked={row.original.enabled}
              onCheckedChange={handleToggle}
              disabled={isUpdating}
            />
            <ActionsDropdown actions={actions} />
          </div>
        );
      },
    },
  ];

  return (
    <div className="px-6 py-6">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold">Patch Management</h1>
          <p className="text-muted-foreground mt-1">
            Configure and manage patching policies for your devices
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Policy
        </Button>
      </div>

      <Tabs defaultValue="policies" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="policies">Policies</TabsTrigger>
          <TabsTrigger value="updates">Available Updates</TabsTrigger>
        </TabsList>
        
        <TabsContent value="policies">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            <div className="text-center py-4 text-destructive">
              Error loading policies
            </div>
          ) : (
            <DataTable
              data={policies || []}
              columns={columns}
              enableRowSelection={true}
              enableColumnResizing={true}
              enableSorting={true}
              columnSizing={columnSizing}
              onColumnSizingChange={setColumnSizing}
              emptyMessage="No patch policies found. Create your first policy to get started."
            />
          )}
        </TabsContent>
        
        <TabsContent value="updates">
          <div className="bg-card rounded-lg border shadow-sm p-4">
            <AvailableUpdates />
          </div>
        </TabsContent>
      </Tabs>

      <CreatePatchPolicyDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onPolicyCreated={handlePolicyCreated}
      />

      <TagAssignmentDialog
        open={tagAssignmentOpen}
        onOpenChange={setTagAssignmentOpen}
        resourceType="patch_policy"
        resourceId={selectedPolicyId || ""}
        onComplete={() => {
          queryClient.invalidateQueries({ queryKey: ["patch-policies"] });
        }}
      />

      <TagRemovalDialog
        open={tagRemovalOpen}
        onOpenChange={setTagRemovalOpen}
        deviceId={selectedPolicyId || ""}
        onComplete={() => {
          queryClient.invalidateQueries({ queryKey: ["patch-policies"] });
        }}
      />
    </div>
  );
}